import { useTranslation } from '@pancakeswap/localization'
import { AutoColumn, Box, Button, Card, Flex, Heading, Text, useMatchBreakpoints } from '@pancakeswap/uikit'
import Page from 'components/Layout/Page'
import Search from 'views/Info/components/InfoSearch'
import dayjs from 'dayjs'
import { useActiveChainId } from 'hooks/useActiveChainId'
import useTheme from 'hooks/useTheme'
import { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import BarChart from './components/BarChart/alt'
import { DarkGreyCard } from './components/Card'
import LineChart from './components/LineChart/alt'
import Percent from './components/Percent'
import PoolTable from './components/PoolTable'
import { RowBetween, RowFixed } from './components/Row'
import TokenTable from './components/TokenTable'
import TransactionsTable from './components/TransactionsTable'
import { ChartCardsContainer, MonoSpace, ProtocolWrapper } from './components/shared'
import {
  useProtocolChartData,
  useProtocolData,
  useProtocolTransactionData,
  useTopPoolsData,
  useTopTokensData,
} from './hooks'
import { useTransformedVolumeData } from './hooks/chart'
import { VolumeWindow } from './types'
import { notEmpty } from './utils'
import { getPercentChange } from './utils/data'
import { unixToDate } from './utils/date'
import { formatDollarAmount } from './utils/numbers'

const Wrapper = styled.div`
  @media screen and (max-width: 1280px) {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: center;
    gap: 1em;
    margin-top: 1em;
  }
`

export default function Home() {
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])
  const { isMobile } = useMatchBreakpoints()

  const { theme } = useTheme()

  const protocolData = useProtocolData()
  const transactionData = useProtocolTransactionData()
  const topTokensData = useTopTokensData()
  const topPoolsData = useTopPoolsData()
  const chartData = useProtocolChartData()
  const { chainId } = useActiveChainId()
  const { t } = useTranslation()

  const [volumeHover, setVolumeHover] = useState<number | undefined>()
  const [liquidityHover, setLiquidityHover] = useState<number | undefined>()
  const [leftLabel, setLeftLabel] = useState<string | undefined>()
  const [rightLabel, setRightLabel] = useState<string | undefined>()
  const now = dayjs()

  useEffect(() => {
    setLiquidityHover(undefined)
    setVolumeHover(undefined)
  }, [chainId])

  useEffect(() => {
    if (liquidityHover === undefined && protocolData) {
      setLiquidityHover(protocolData.tvlUSD)
    }
  }, [liquidityHover, protocolData])

  const formattedTvlData = useMemo(() => {
    if (chartData) {
      return chartData.map((day) => {
        return {
          time: unixToDate(day.date),
          value: day.tvlUSD,
        }
      })
    }
    return []
  }, [chartData])

  const formattedVolumeData = useMemo(() => {
    if (chartData) {
      return chartData.map((day) => {
        return {
          time: unixToDate(day.date),
          value: day.volumeUSD,
        }
      })
    }
    return []
  }, [chartData])

  const weeklyVolumeData = useTransformedVolumeData(chartData, 'week')
  const monthlyVolumeData = useTransformedVolumeData(chartData, 'month')
  const [volumeWindow, setVolumeWindow] = useState(VolumeWindow.daily)

  const formattedTokens = useMemo(() => {
    if (topTokensData)
      return Object.values(topTokensData)
        .map((d) => d)
        .filter(notEmpty)
        .filter((d) => d)
    // .filter((d) => d.tvlUSD > 0)
    return []
  }, [topTokensData])

  const poolDatas = useMemo(() => {
    if (topPoolsData)
      return Object.values(topPoolsData)
        .map((p) => p)
        .filter(notEmpty)
    return []
  }, [topPoolsData])

  const tvlValue = useMemo(() => {
    return formatDollarAmount(liquidityHover, 2, true)
  }, [liquidityHover])

  return (
    <>
      <Page>
        <Heading style={{ flex: 1 }} scale="lg" mb="16px">
          <Flex justifyContent="space-between" alignItems="center" mb="16px">
            <span>{t('Agni Info & Analytics')}</span>
            <Search />
          </Flex>
        </Heading>

        <Flex style={{ gap: isMobile ? '16px' : '59px' }} flexDirection="column" alignItems="center">
          <ChartCardsContainer>
            <Card>
              <LineChart
                data={formattedTvlData}
                height={220}
                minHeight={332}
                // color={theme.colors.primary}
                value={liquidityHover}
                label={leftLabel}
                setValue={setLiquidityHover}
                setLabel={setLeftLabel}
                topLeft={
                  <AutoColumn gap="4px">
                    <Text fontSize="14px" style={{ color: 'rgba(255, 255, 255, 0.45)' }}>
                      {t('TVL')}
                    </Text>
                    <Text fontSize="32px">
                      <MonoSpace>{tvlValue}</MonoSpace>
                    </Text>
                    <Text fontSize="14px" height="14px" style={{ color: 'rgba(255, 255, 255, 0.45)' }}>
                      <MonoSpace>{leftLabel ?? now.format('MMM D, YYYY')} (UTC)</MonoSpace>
                    </Text>
                  </AutoColumn>
                }
              />
            </Card>
            <Card>
              <BarChart
                height={200}
                minHeight={332}
                data={
                  volumeWindow === VolumeWindow.monthly
                    ? monthlyVolumeData
                    : volumeWindow === VolumeWindow.weekly
                    ? weeklyVolumeData
                    : formattedVolumeData
                }
                color={theme.colors.primary}
                setValue={setVolumeHover}
                setLabel={setRightLabel}
                value={volumeHover}
                label={rightLabel}
                activeWindow={volumeWindow}
                topRight={
                  <RowFixed
                    style={{
                      background: 'rgba(255, 255, 255, 0.08)',
                      borderRadius: '8px',
                      marginLeft: '-40px',
                      marginTop: '8px',
                      fontSize: 14,
                      fontWeight: 700,
                    }}
                  >
                    <Button
                      style={{
                        width: '44px',
                        height: '26px',
                        borderRadius: 8,
                        fontSize: 14,
                        fontWeight: 700,
                      }}
                      scale="sm"
                      variant={volumeWindow === VolumeWindow.daily ? 'primary' : 'text'}
                      onClick={() => setVolumeWindow(VolumeWindow.daily)}
                    >
                      D
                    </Button>
                    <Button
                      scale="sm"
                      style={{
                        width: '44px',
                        height: '26px',
                        borderRadius: 8,
                        marginLeft: '8px',
                        fontSize: 14,
                        fontWeight: 700,
                      }}
                      variant={volumeWindow === VolumeWindow.weekly ? 'primary' : 'text'}
                      onClick={() => setVolumeWindow(VolumeWindow.weekly)}
                    >
                      W
                    </Button>
                    <Button
                      variant={volumeWindow === VolumeWindow.monthly ? 'primary' : 'text'}
                      scale="sm"
                      style={{
                        width: '44px',
                        height: '26px',
                        borderRadius: 8,
                        marginLeft: '8px',
                        fontSize: 14,
                        fontWeight: 700,
                      }}
                      onClick={() => setVolumeWindow(VolumeWindow.monthly)}
                    >
                      M
                    </Button>
                  </RowFixed>
                }
                topLeft={
                  <AutoColumn gap="4px">
                    <Text fontSize="14px" style={{ color: 'rgba(255, 255, 255, 0.45)' }}>
                      {t('Volume')}
                    </Text>
                    <Text fontSize="32px">
                      <MonoSpace>
                        {volumeHover
                          ? formatDollarAmount(volumeHover)
                          : formatDollarAmount(formattedVolumeData[formattedVolumeData.length - 1]?.value, 2)}
                      </MonoSpace>
                    </Text>
                    <Text fontSize="14px" height="14px" style={{ color: 'rgba(255, 255, 255, 0.45)' }}>
                      <MonoSpace>{rightLabel ?? now.format('MMM D, YYYY')} (UTC)</MonoSpace>
                    </Text>
                  </AutoColumn>
                }
              />
            </Card>
          </ChartCardsContainer>
        </Flex>
        <ProtocolWrapper style={{ marginBottom: '40px' }}>
          <DarkGreyCard>
            <RowBetween>
              <RowFixed>
                <RowFixed mr="20px">
                  <Text mr="4px">{t('Volume 24H')}: </Text>
                  <Text mr="4px">{formatDollarAmount(formattedVolumeData[formattedVolumeData.length - 1]?.value)}</Text>
                  <Percent
                    value={getPercentChange(
                      formattedVolumeData[formattedVolumeData.length - 1]?.value.toString(),
                      formattedVolumeData[formattedVolumeData.length - 2]?.value.toString(),
                    )}
                    wrap
                  />
                </RowFixed>
                <RowFixed mr="20px">
                  <Text mr="4px">{t('Fees 24H')}: </Text>
                  <Text mr="4px">{formatDollarAmount(protocolData?.feesUSD)}</Text>
                  <Percent value={protocolData?.feeChange} wrap />
                </RowFixed>
                <Box>
                  <RowFixed mr="20px">
                    <Text mr="4px">{t('TVL')}: </Text>
                    <Text mr="4px">{formatDollarAmount(protocolData?.tvlUSD)}</Text>
                    <Percent value={protocolData?.tvlUSDChange} wrap />
                  </RowFixed>
                </Box>
              </RowFixed>
            </RowBetween>
          </DarkGreyCard>
        </ProtocolWrapper>
        <Wrapper>
          <PoolTable poolDatas={poolDatas} />
          <TokenTable tokenDatas={formattedTokens} />
          {transactionData ? <TransactionsTable transactions={transactionData} /> : null}
        </Wrapper>
      </Page>
    </>
  )
}
